// components/SearchProjects.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/search.css';
import BookmarkModal from './modals/bookmark-modal';
import EmailModal from './modals/email-modal';
import WhatsAppModal from './modals/whatsapp-modal';
import OverviewBottomSheet from './modals/overview_modal';
import HighlightBottomSheet from './modals/highlight_modal';
import BookmarkAlert from './modals/succes_alert';


const BASE_URL = `${process.env.REACT_APP_API_BASE}/`;
const SEARCH_API = 'api/all_projects/';
const BOOKMARK_API = 'api/milestone-updates/';

const normalizeResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.results?.features && Array.isArray(payload.results.features)) {
    return payload.results.features;
  }
  if (payload?.results && Array.isArray(payload.results)) return payload.results;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
};

const mapFeatureToProject = (feature) => {
  const properties = feature?.properties ?? {};
  return {
    id: feature?.id ?? properties?.id,
    ...properties,
    // For convenience, add these aliases:
    status: properties?.is_ongoing,
    cover: properties?.cover,
    company: properties?.company_name ?? properties?.company,
  };
};

const fetchProjects = async (query, signal) => {

  const url = new URL(SEARCH_API, BASE_URL);
  url.searchParams.set('search', query);

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Unable to fetch search results.');
  }

  const payload = await response.json();
  return normalizeResults(payload);
};

const SearchProjects = ({ onClose }) => {
  const navigate = useNavigate();
  // Modal state for new flow (must be inside component)
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [showHighlightModal, setShowHighlightModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const skipNextEffect = useRef(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showBookmarkAlert, setShowBookmarkAlert] = useState(false);
  const [bookmarkAlertMethod, setBookmarkAlertMethod] = useState('');

  const suggestedSearches = ['NYL99-USA', 'TSLA-01A/25', 'VGHS8-TZA'];

  const flagEmoji = (countryCode) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65)
      );
  };

  // ======================
  // Custom Hook: Get country flag from lat/lng
  // ======================
  const useCountryFromLatLng = (lat, lng) => {
    const [countryCode, setCountryCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      if (!lat || !lng) {
        setCountryCode(null);
        setError(null);
        return;
      }

      // Skip if we already have the code
      if (countryCode) return;

      const fetchCountry = async () => {
        setLoading(true);
        setError(null);

        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`;
          const response = await fetch(url, {
            headers: { 'User-Agent': 'centria-landing/1.0' },
          });

          if (!response.ok) throw new Error('Failed to fetch country');

          const data = await response.json();
          const code = data.address?.country_code?.toUpperCase() || null;

          setCountryCode(code);
        } catch (err) {
          console.warn('Country lookup failed for', lat, lng, err);
          setCountryCode(null);
          setError('Could not determine country');
        } finally {
          setLoading(false);
        }
      };

      fetchCountry();
    }, [lat, lng]);   // Removed countryCode from deps to prevent loops

    // Generate emoji flag
    const flagEmoji = useMemo(() => {
      if (!countryCode || countryCode.length !== 2) return null;
      return String.fromCodePoint(
        ...countryCode.split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
      );
    }, [countryCode]);

    return { countryCode, flagEmoji, loading, error };
  };


  // New modal flow: overview -> highlight -> bookmark -> email/whatsapp
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setShowBookmarkModal(false);
    if (option === 'email') {
      setShowEmailModal(true);
    } else if (option === 'whatsapp') {
      const savedNumber = localStorage.getItem('whatsappNumber');
      const savedRemember = localStorage.getItem('whatsappRememberMe');
      if (savedNumber && savedRemember === 'true') {
        handleContinue({ option: 'whatsapp', number: savedNumber });
      } else {
        setShowWhatsAppModal(true);
      }
    }
  };


  // Navigate to ProjectPublish component
  const [highlightAnnouncement, setHighlightAnnouncement] = useState(null);
  const handleProjectClick = (project) => {
    const projectCode = project?.code;
    if (!projectCode) {
      return;
    }

    navigate(`/publish/${encodeURIComponent(projectCode)}`);
  };

  // Open bookmark modal from overview or highlight modal
  const handleOpenBookmarkModal = () => {
    setShowOverviewModal(false);
    setShowHighlightModal(false);
    setTimeout(() => setShowBookmarkModal(true), 300);
  };

  // When overview modal closes, show highlight modal if action is 'next' and pass announcement
  const handleOverviewClose = (action, announcement) => {
    setShowOverviewModal(false);
    if (action === 'next') {
      setHighlightAnnouncement(announcement || null);
      setTimeout(() => setShowHighlightModal(true), 300); // match animation
    }
  };

  // When highlight modal closes, show bookmark modal
  const handleHighlightClose = () => {
    setShowHighlightModal(false);
    // setTimeout(() => setShowBookmarkModal(true), 300);
  };


  const handleContinue = (overrides = {}) => {
    const activeOption = overrides.option ?? selectedOption;
    const activeNumber = overrides.number ?? whatsappNumber;

    // Handle continue action
    console.log('Continue with:', activeOption,
      activeOption === 'email' ? email : activeNumber,
      'Remember me:', rememberMe);

    // Save email to localStorage if 'remember me' is checked
    if (activeOption === 'email' && rememberMe && email) {
      localStorage.setItem('rememberedEmail', email);
    } else if (activeOption === 'email' && !rememberMe) {
      localStorage.removeItem('rememberedEmail');
    }

    let body;
    if (activeOption === 'whatsapp' && selectedProject && activeNumber) {
      body = {
        project_id: selectedProject.id,
        phone_number: activeNumber,
        active: true,
      };
    } else {
      body = {
        method: activeOption,
        value: activeOption === 'email' ? email : activeNumber,
        // rememberMe,
      };
    }

    const url = new URL(BOOKMARK_API, BASE_URL);
    fetch(url.toString(), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Bookmarking Failed');
        }
        return res.json();
      })
      .then(() => {
        setBookmarkAlertMethod(activeOption);
        setShowBookmarkAlert(true);
        closeAllModals();
      })
      .catch((err) => {
        console.error(err);
        alert('Error bookmarking project. Please try again.');
      });

    // Here you would typically send this data to your backend
    // alert(`Notification preference saved: ${selectedOption === 'email' ? email : whatsappNumber}`);

    closeAllModals();
  };

  const closeAllModals = () => {
    setShowBookmarkModal(false);
    setShowEmailModal(false);
    setShowWhatsAppModal(false);
    setSelectedOption('');
    setRememberMe(false);
    setEmail('');
    setWhatsappNumber('');
  };


  useEffect(() => {
    // Load recent searches from localStorage
    const savedSearches = localStorage.getItem('recentProjectSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
    // Load remembered email if present
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const updateRecentSearches = (term) => {
    const updatedSearches = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentProjectSearches', JSON.stringify(updatedSearches));
  };

  const performSearch = async (term, { storeRecent = true, signal } = {}) => {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) {
      setProjects([]);
      setErrorMessage('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const results = await fetchProjects(trimmedTerm, signal);
      const mappedResults = results.map((item) => (
        item?.type === 'Feature' || item?.properties ? mapFeatureToProject(item) : item
      ));
      setProjects(mappedResults);
      if (storeRecent) {
        updateRecentSearches(trimmedTerm);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setProjects([]);
      setErrorMessage(error.message || 'Unable to fetch search results.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (skipNextEffect.current) {
      skipNextEffect.current = false;
      return;
    }

    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
      setProjects([]);
      setErrorMessage('');
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const debounceTimer = setTimeout(() => {
      performSearch(trimmedTerm, { storeRecent: false, signal: controller.signal });
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [searchTerm]);

  const handleSuggestedSearch = (suggestion) => {
    skipNextEffect.current = true;
    setSearchTerm(suggestion);
    performSearch(suggestion, { storeRecent: true });
  };

  const clearSearch = () => {
    setSearchTerm('');
    setProjects([]);
    setErrorMessage('');
  };

  return (
    <div className="container">
      <div className="header">
        <div className="headerTop">
          <h1 className="title"><span className="title-brand">ConRa</span> Search</h1>
        </div>
        <p className="subtitle">
          Search our Global Network to Connect with the Projects, Partners and Innovators shaping the Built Environment
        </p>
      </div>
      <div className="searchInputContainer">
        <svg className="searchIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="var(--color-primary)" strokeWidth="2" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className="searchInput"
          placeholder="Enter unique project ID (e.g NVDA001)"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {searchTerm && (
          <button className="clearButton" onClick={clearSearch}>
            ✕
          </button>
        )}
      </div>

      {recentSearches.length > 0 && (
        <div className="recentSearches">
          <h3 className="recentTitle">Recent Searches</h3>
          <div className="recentTags">
            {recentSearches.map((search, index) => (
              <button
                key={index}
                className="recentTag"
                onClick={() => handleSuggestedSearch(search)}
              >
                {search}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="projectsSection">
        <h2 className="projectsTitle">
          {searchTerm ? `Search results (${projects.length}):` : 'Search results:'}
        </h2>

        {isLoading && (
          <div className="statusMessage">Searching projects...</div>
        )}

        {errorMessage && !isLoading && (
          <div className="statusMessage errorMessage">{errorMessage}</div>
        )}

        {!isLoading && !errorMessage && (
          <div className="projectsGrid">
            {projects.map((project, index) => (
              <div
                key={project.id ?? `${project.code}-${index}`}
                className="projectCard"
                onClick={() => handleProjectClick(project)}
              >
                {project.cover && (
                  <div className="projectCover">
                    <img src={project.cover} alt={project.name ?? 'Project cover'} />
                    <span className="projectEcoBadge" aria-label="Sustainable project">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M5 19C5 12 10 6 19 5c1 9-5 14-12 14" fill="#22c55e" />
                        <path d="M6 18c2-4 5-7 9-9" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    </span>
                  </div>
                )}
                <div className="projectBody">
                  <div className="projectNameRow">
                    <h3 className="projectName">{project.name ?? 'Untitled Project'}</h3>
                    {project.country_code && (
                      <span className="projectFlag">{flagEmoji(project.country_code)}</span>
                    )}
                  </div>
                  <p className="projectDescription">{project.description ?? 'No description available.'}</p>
                  <div className="projectDivider" />
                  <div className="projectHeader">
                    <span className="projectClient">
                      <span className="companyAvatar" aria-hidden="true">
                        {(project.company ?? 'U').charAt(0).toUpperCase()}
                      </span>
                      <span className="companyName">{project.company ?? 'Unknown'}</span>
                      <svg className="verifiedBadge" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Verified">
                        <path d="M12 2l2.4 2.2 3.2-.5 1 3.1 2.9 1.5-1.2 3 1.2 3-2.9 1.5-1 3.1-3.2-.5L12 22l-2.4-2.6-3.2.5-1-3.1-2.9-1.5 1.2-3-1.2-3 2.9-1.5 1-3.1 3.2.5L12 2z" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinejoin="round" />
                        <path d="M8.8 12.2l2.1 2.1 4.3-4.4" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="projectCode">{project.code ?? 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !errorMessage && projects.length === 0 && searchTerm && (
          <div className="noResults">
            <p>No projects found for "{searchTerm}"</p>
            <button className="clearSearchButton" onClick={clearSearch}>
              Clear search
            </button>
          </div>
        )}
      </div>


      {/* Modals: Overview -> Highlight -> Bookmark -> Email/WhatsApp */}
      <OverviewBottomSheet
        isOpen={showOverviewModal}
        onClose={handleOverviewClose}
        onBookmark={handleOpenBookmarkModal}
        projectData={selectedProject}
      />
      <HighlightBottomSheet
        isOpen={showHighlightModal}
        onClose={handleHighlightClose}
        onBookmark={handleOpenBookmarkModal}
        projectData={selectedProject}
        announcement={highlightAnnouncement}
      />
      <BookmarkModal
        isOpen={showBookmarkModal}
        onClose={closeAllModals}
        onSelectOption={handleOptionSelect}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
      <EmailModal
        isOpen={showEmailModal}
        onClose={closeAllModals}
        onContinue={handleContinue}
        email={email}
        setEmail={setEmail}
        rememberMe={rememberMe}
        setRememberMe={setRememberMe}
      />
      <WhatsAppModal
        isOpen={showWhatsAppModal}
        onClose={closeAllModals}
        onContinue={handleContinue}
        whatsappNumber={whatsappNumber}
        setWhatsappNumber={setWhatsappNumber}
        rememberMe={rememberMe}
        setRememberMe={setRememberMe}
      />
      {showBookmarkAlert && (
        <BookmarkAlert
          projectCode={selectedProject?.code}
          method={bookmarkAlertMethod}
          onClose={() => setShowBookmarkAlert(false)}
        />
      )}
    </div>
  );
};

export default SearchProjects;